/**
 * Credit Memo / Loan Submission DTOs (Issue #1466)
 *
 * Per-case "loan submission" artifact replacing the Excel credit memos
 * lenders write today. State machine:
 *   DRAFT → SUBMITTED → (APPROVED | REJECTED) → ARCHIVED
 *
 * Multiple memos per case across the loan lifecycle (initial draft +
 * committee revision + post-condition update); the latest non-archived
 * row is the canonical view, older versions stay for audit.
 */

import { z } from 'zod';
import { CreditMemoNarrativesSchema } from './credit-memo-narratives.dto';

// ============================================================================
// Status enum
// ============================================================================

export const CreditMemoStatus = {
  DRAFT: 'DRAFT',
  SUBMITTED: 'SUBMITTED',
  APPROVED: 'APPROVED',
  REJECTED: 'REJECTED',
  ARCHIVED: 'ARCHIVED',
} as const;
export type CreditMemoStatus = (typeof CreditMemoStatus)[keyof typeof CreditMemoStatus];

const CreditMemoStatusEnum = z.enum(Object.values(CreditMemoStatus) as [CreditMemoStatus, ...CreditMemoStatus[]]);

// ============================================================================
// Narrative section enum — canonical section identifiers used by
// i18n labels and historical seed data. The runtime `narratives` field on
// `CreditMemoDto` is now a typed structure (see `credit-memo-narratives.dto.ts`)
// rather than a `Record<NarrativeSection, string>` blob, but these section
// keys are still useful as stable identifiers when a feature wants to
// reference a canonical section by name (e.g. i18n labels in
// `creditMemo.section.*`).
// ============================================================================

export const CreditMemoNarrativeSection = {
  BORROWER_SUMMARY: 'borrower_summary',
  PROPERTY_SUMMARY: 'property_summary',
  FINANCIAL_SUMMARY: 'financial_summary',
  RISK_FACTORS: 'risk_factors',
  MITIGANTS: 'mitigants',
  RECOMMENDATION: 'recommendation',
} as const;
export type CreditMemoNarrativeSection = (typeof CreditMemoNarrativeSection)[keyof typeof CreditMemoNarrativeSection];

export const CREDIT_MEMO_NARRATIVE_SECTIONS = Object.values(CreditMemoNarrativeSection);

/**
 * Snapshot blob shape — all keys are optional / nullable because the
 * structured fields on Case are themselves optional during early stages.
 * Stored as JSON so we can extend the snapshot without a migration.
 */
export const CreditMemoSnapshotSchema = z.record(z.string().min(1).max(64), z.unknown()).nullable();
export type CreditMemoSnapshot = z.infer<typeof CreditMemoSnapshotSchema>;

// ============================================================================
// Full row schema
// ============================================================================

export const CreditMemoSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  caseId: z.string().uuid(),
  version: z.number().int().positive(),
  status: CreditMemoStatusEnum,
  /**
   * Typed narratives blob — see `credit-memo-narratives.dto.ts` for the
   * full shape (sections, citations, reasoning callouts). `null` means
   * the memo hasn't had narratives generated yet (DRAFT before first
   * Astor run, or pre-Wave-2 legacy rows during migration).
   */
  narratives: CreditMemoNarrativesSchema.nullable(),
  snapshot: CreditMemoSnapshotSchema,
  submittedAt: z.union([z.string(), z.date()]).nullable(),
  submittedBy: z.string().nullable(),
  approvedAt: z.union([z.string(), z.date()]).nullable(),
  approvedBy: z.string().nullable(),
  approvalNote: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.union([z.string(), z.date()]).optional(),
  updatedAt: z.union([z.string(), z.date()]).optional(),
});
export type CreditMemoDto = z.infer<typeof CreditMemoSchema>;

// ============================================================================
// Input schemas
// ============================================================================

/**
 * Create a new DRAFT memo for a case. Both `narratives` and `snapshot`
 * are optional — a memo can start blank and the underwriter fills in
 * narratives over time. Snapshot is auto-frozen at submit time if the
 * draft didn't carry one (see CreditMemoService.submit).
 */
export const CreateCreditMemoInputSchema = z.object({
  caseId: z.string().uuid(),
  narratives: CreditMemoNarrativesSchema.nullable().optional(),
  snapshot: CreditMemoSnapshotSchema.optional(),
});
export type CreateCreditMemoInput = z.infer<typeof CreateCreditMemoInputSchema>;

/**
 * Patch narratives or snapshot on a DRAFT memo. Service throws
 * ForbiddenException if the memo has progressed past DRAFT.
 */
export const UpdateCreditMemoInputSchema = z.object({
  narratives: CreditMemoNarrativesSchema.nullable().optional(),
  snapshot: CreditMemoSnapshotSchema.optional(),
});
export type UpdateCreditMemoInput = z.infer<typeof UpdateCreditMemoInputSchema>;

export const SubmitCreditMemoInputSchema = z.object({
  id: z.string().uuid(),
});
export type SubmitCreditMemoInput = z.infer<typeof SubmitCreditMemoInputSchema>;

export const ApproveCreditMemoInputSchema = z.object({
  id: z.string().uuid(),
  /** Optional note shown on the approval audit-footer. */
  note: z.string().max(2000).optional(),
});
export type ApproveCreditMemoInput = z.infer<typeof ApproveCreditMemoInputSchema>;

export const RejectCreditMemoInputSchema = z.object({
  id: z.string().uuid(),
  /**
   * Required reason. The service enforces non-empty, but we also enforce
   * here so the gateway returns a 400 (zodError) rather than a 500 from
   * the gRPC layer.
   */
  note: z.string().min(1).max(2000),
});
export type RejectCreditMemoInput = z.infer<typeof RejectCreditMemoInputSchema>;
